import axios from 'axios';
import { accountManager } from '../../utils/accountManager';

/**
 * Shell HTTP client (axios) for shell-level cross-cutting services that remain
 * in the shell: auth, partnerships, and partner-guests.
 *
 * AUTH-PATH NOTE: This client reads its credentials from the SAME source as the
 * plugin-sdk `getApiHeaders()` fetch path used by UI plugins. Both read the
 * `accounts` + `activeAccountEmail` localStorage keys (via accountManager here,
 * and inline in @lognai/plugin-sdk's api-client.ts) and emit identical headers:
 *   - `Authorization: Bearer <session.accessToken>`
 *   - `X-Company-ID: <session.activeCompanyId>`
 * The only differences are transport (axios vs fetch) and that this client adds
 * a 401 -> clear-session -> redirect-to-/login interceptor. The token sources are
 * already aligned, so no auth change is required for the remaining shell services.
 */

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4611',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const session = accountManager.getActiveSession();

  // Debug logging for development
  if (import.meta.env.DEV) {
    console.debug('[API Client] Session check:', {
      hasSession: !!session,
      activeEmail: accountManager.getActiveAccountEmail(),
      allAccounts: Object.keys(accountManager.getAllAccounts()),
      accessToken: session?.accessToken ? `${session.accessToken.substring(0, 20)}...` : null,
    });
  }

  if (session?.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }

  // Add company header if user has an active company
  if (session?.activeCompanyId) {
    config.headers['X-Company-ID'] = session.activeCompanyId;
  }

  // Debug logging for development
  if (import.meta.env.DEV) {
    console.debug('[API Client] Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!config.headers.Authorization,
      companyId: config.headers['X-Company-ID'] || null,
    });
  }

  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Debug logging for development
    if (import.meta.env.DEV && error.response) {
      console.error('[API Client] Request failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        errorData: error.response?.data,
      });
    }

    if (error.response?.status === 401) {
      // Clear all auth storage
      accountManager.clearAll();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
